import type { Data } from "@eai-toolkit/client";
import { Clear, EditNote, VisibilityOutlined } from "@mui/icons-material";
import { Box, CircularProgress, IconButton, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";

import {
  useValidatedResourceField,
  ValidationTextField,
} from "@components/ValidationContext/ValidationContext";

import DescriptiveTooltip from "../../../components/DescriptiveTooltip";
import ResourceSelector from "../../../components/ResourceSelector/ResourceSelector";
import {
  checkDataAccessLevel,
  type DataAccessLevel,
} from "../../../helpers/api";
import { SHARED_BOOKMARK_CACHE_IDS } from "../../../hooks/useResourceBookmarks";
import { useGetDataObjectByIdQuery } from "../../../store/api/dataApi";
import { HTTP_FORBIDDEN, HTTP_NOT_FOUND } from "../../../store/api/types";
import {
  ACCESS_MODE_READ_ONLY,
  ACCESS_MODE_READ_WRITE,
  type AccessMode,
  isAccessMode,
} from "../constants";
import { validateMountPath, validateSourcePath } from "../utils";
import { FORM_COLLAPSE_PX } from "./NewJobFormLayout";

export const DATA_MOUNTS_CACHE_ID = SHARED_BOOKMARK_CACHE_IDS.data;

export function getOptionLabel(data: Data) {
  return data.fullName || data.id;
}

/**
 * Grid for a single data-mount row. 2-up at narrow widths; single line at
 * >=FORM_COLLAPSE_PX, sharing the same --row width as 2-column field rows.
 */
const DataMountEntry = styled("div")(({ theme }) => ({
  display: "grid",
  width: "100%",
  maxWidth: "var(--row)",
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(1.5),
  alignItems: "start",
  justifyContent: "start",
  gridTemplateColumns: "minmax(0, var(--col)) minmax(0, var(--col)) auto",
  // Pin the clear button to the right of the first row regardless of wrapping.
  "& .data": { gridColumn: 1, gridRow: 1 },
  "& .sourcePath": { gridColumn: 2, gridRow: 1 },
  "& .mountPath": { gridColumn: 1, gridRow: 2 },
  "& .access": { gridColumn: 2, gridRow: 2 },
  "& .clear": { gridColumn: 3, gridRow: 1, marginTop: 6 },
  [theme.breakpoints.up(FORM_COLLAPSE_PX)]: {
    gridTemplateColumns:
      "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 100px) auto",
    "& .data": { gridColumn: 1, gridRow: 1 },
    "& .sourcePath": { gridColumn: 2, gridRow: 1 },
    "& .mountPath": { gridColumn: 3, gridRow: 1 },
    "& .access": { gridColumn: 4, gridRow: 1 },
    "& .clear": { gridColumn: 5, gridRow: 1 },
  },
}));

type Props = {
  dataId: string;
  sourcePath: string;
  mountPath: string;
  accessMode: AccessMode;
  index: number;
  rowKey: number;
  isMountPathDuplicated: boolean;
  initialRecents: Data[];
  onInitialRecentsSeeded: () => void;
  onDataObjectSelected: (
    selection: Data | string | null,
    index: number,
  ) => void;
  onDataResolved: (index: number, data: Data | null) => void;
  onDataMountPathUpdated: (mountPath: string) => void;
  onSourcePathUpdated: (sourcePath: string) => void;
  onAccessModeUpdated: (accessMode: AccessMode) => void;
  onCleared: () => void;
};

function DataMountsFieldRow({
  dataId,
  sourcePath,
  mountPath,
  accessMode,
  index,
  rowKey,
  isMountPathDuplicated,
  initialRecents,
  onInitialRecentsSeeded,
  onDataObjectSelected,
  onDataResolved,
  onDataMountPathUpdated,
  onSourcePathUpdated,
  onAccessModeUpdated,
  onCleared,
}: Props) {
  const { data: singleDataObject, error: singleDataObjectError } =
    useGetDataObjectByIdQuery({ id: dataId || "" }, { skip: !dataId });

  useEffect(() => {
    onDataResolved(index, singleDataObject ?? null);
  }, [index, singleDataObject, onDataResolved]);

  const [dataAccessLevel, setDataAccessLevel] =
    useState<DataAccessLevel | null>(null);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (dataId && singleDataObject) {
      const checkAccess = async () => {
        setCheckingAccess(true);
        try {
          const result = await checkDataAccessLevel(singleDataObject.urn);
          setDataAccessLevel(result);
        } catch (error) {
          console.error("Error checking data access level:", error);
        } finally {
          setCheckingAccess(false);
        }
      };

      checkAccess();
    } else {
      setDataAccessLevel(null);
    }
  }, [dataId, singleDataObject]);

  // When the single data object fetch fails (e.g. user lacks data:get access),
  // assume no write access so the rw option is disabled.
  useEffect(() => {
    if (singleDataObjectError && !dataAccessLevel) {
      setDataAccessLevel({ hasWriteAccess: false, urn: "" });
    }
  }, [singleDataObjectError, dataAccessLevel]);

  // Determine error message from RTK Query error
  const dataLoadErrorMessage = useMemo(() => {
    if (!singleDataObjectError) return null;
    if (
      typeof singleDataObjectError === "object" &&
      "status" in singleDataObjectError
    ) {
      const status = (singleDataObjectError as { status: number | string })
        .status;
      if (status === HTTP_FORBIDDEN) return "You cannot access this data";
      if (status === HTTP_NOT_FOUND) return "Data does not appear to exist";
    }
    return "Error loading data";
  }, [singleDataObjectError]);

  const dataIdError = !dataId
    ? "Data selection cannot be blank"
    : dataLoadErrorMessage;

  const { visibleErrorMessage, handleFocus, handleBlur } =
    useValidatedResourceField({
      instanceId: `data:id:${rowKey}`,
      valueForValidation: dataId,
      error: dataIdError,
    });

  const sourcePathError = validateSourcePath(sourcePath) ?? null;
  const individualMountPathError = validateMountPath(mountPath);
  const mountPathError =
    individualMountPathError ||
    (isMountPathDuplicated
      ? {
          message: "Mount path must be unique",
          groupId: `data:mountpath:${mountPath}`,
        }
      : null);

  return (
    <DataMountEntry key={rowKey}>
      <Box className="data" onFocus={handleFocus} onBlur={handleBlur}>
        <ResourceSelector<Data>
          resourceType="data"
          cacheId={DATA_MOUNTS_CACHE_ID}
          value={singleDataObject ?? null}
          onChange={(value) => onDataObjectSelected(value, index)}
          freeSolo
          onFreeSoloBlur={(rawValue) => onDataObjectSelected(rawValue, index)}
          initialRecents={initialRecents}
          onInitialRecentsSeeded={onInitialRecentsSeeded}
          label="Data"
          placeholder="Search for a dataset..."
          required
          error={visibleErrorMessage}
          startIcon={
            dataId ? (
              <DescriptiveTooltip
                title={
                  checkingAccess
                    ? "Checking access..."
                    : dataAccessLevel?.hasWriteAccess
                      ? "Read/Write Access"
                      : "Read-Only Access"
                }
                open={hover}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                placement="right"
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {checkingAccess ? (
                    <CircularProgress size={16} />
                  ) : dataAccessLevel ? (
                    dataAccessLevel.hasWriteAccess ? (
                      <EditNote color="primary" fontSize="small" />
                    ) : (
                      <VisibilityOutlined color="primary" fontSize="small" />
                    )
                  ) : null}
                </Box>
              </DescriptiveTooltip>
            ) : undefined
          }
        />
      </Box>
      <ValidationTextField
        className="sourcePath"
        instanceId={`data:sourcepath:${rowKey}`}
        label="Path in data"
        variant="outlined"
        value={sourcePath}
        valueForValidation={sourcePath}
        placeholder="/ (root)"
        InputLabelProps={{ shrink: true }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          // Mounts serialize as `<dataId><sourcePath>:<mountPath>:<mode>` with
          // `/` as the split delimiter, so a sourcePath without a leading
          // slash gets concatenated onto the dataId and corrupts it.
          const v = e.target.value;
          onSourcePathUpdated(v && !v.startsWith("/") ? `/${v}` : v);
        }}
        error={sourcePathError}
      />
      <ValidationTextField
        className="mountPath"
        instanceId={`data:mountpath:${rowKey}`}
        required
        label="Path in container"
        variant="outlined"
        value={mountPath}
        valueForValidation={mountPath}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onDataMountPathUpdated(e.target.value)
        }
        error={mountPathError}
        validateOthersOnBlur={[`data:id:${rowKey}`]}
      />
      <ValidationTextField
        className="access"
        instanceId={`data:access:${rowKey}`}
        select
        label="Access"
        variant="outlined"
        value={accessMode}
        valueForValidation={accessMode}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const next = e.target.value;
          if (isAccessMode(next)) {
            onAccessModeUpdated(next);
          }
        }}
        error={
          singleDataObjectError
            ? "No access to this data"
            : dataAccessLevel &&
                !dataAccessLevel.hasWriteAccess &&
                accessMode === ACCESS_MODE_READ_WRITE
              ? "You do not have write access"
              : null
        }
        disabled={!dataId || checkingAccess}
        sx={{ maxWidth: 100 }}
      >
        <MenuItem
          value={ACCESS_MODE_READ_ONLY}
          disabled={!!singleDataObjectError}
        >
          {ACCESS_MODE_READ_ONLY}
        </MenuItem>
        <MenuItem
          value={ACCESS_MODE_READ_WRITE}
          disabled={!!singleDataObjectError || !dataAccessLevel?.hasWriteAccess}
        >
          {ACCESS_MODE_READ_WRITE}
        </MenuItem>
      </ValidationTextField>
      <IconButton className="clear" onClick={onCleared}>
        <Clear />
      </IconButton>
    </DataMountEntry>
  );
}

export default DataMountsFieldRow;
